import { type FC, useMemo } from "react";
import type { InputProps } from "../DynamicInput";
import type { InlineInputStateStyles } from "../inlineForm/inputs/InlineInputState";
import { usePermission } from "../../shared/permissions";
import { CreateDpiaDialog } from "../../pages/DpiaGridPage/CreateDpiaDialog";
import { dpiaGridViewQueryOptions } from "../../api/dpia";
import { DpiaIcon3 } from "../../shared/icons";
import { useGetSortByKey } from "../../shared/SortByKey";
import type { CreateNewDialogProps } from "./MultiAutocomplete";
import { SingleAutocomplete } from "./SingleAutocomplete";
import { Box } from "@mui/material";
import { DynamicGrid } from "../../dynamicGrid";
import { useQuery } from "@tanstack/react-query";

export const SingleDpiaInput: FC<InputProps<string> & { inlineInputStateStyles?: InlineInputStateStyles }> = props => {
  const { hasPermission } = usePermission();
  const dpiaGridViewQuery = useQuery(dpiaGridViewQueryOptions());
  const sortByKey = useGetSortByKey();
  const options = useMemo(
    () =>
      sortByKey(dpiaGridViewQuery.data?.responses.map(dpia => ({ label: dpia.name, value: dpia.id })) ?? [], "label"),
    [dpiaGridViewQuery.data, sortByKey]
  );

  if (!dpiaGridViewQuery.data) return <Box sx={{ height: 36 }} />;

  return (
    <SingleAutocomplete
      {...props}
      inlineInputStateStyles={props.inlineInputStateStyles}
      options={options}
      renderIcon={iconProps => <DpiaIcon3 {...iconProps} />}
      placeholderKey="DynamicForm.Inline.InputPlaceholder"
      chipVariant="entity"
      {...(hasPermission("nova-forms-api:dpia:create")
        ? { CreateNewDialog: CreateNewDpiaDialog }
        : { noOptionsTextKey: "DynamicForm.Option.Empty" })}
    />
  );
};

const CreateNewDpiaDialog: FC<CreateNewDialogProps> = ({ onClose, value, onCreated }) => {
  /** HACK: This code assumes that SingleDpiaInput only is used within a registration */
  const registrationId = DynamicGrid.useSelectedRowId();

  return (
    <CreateDpiaDialog
      onClose={onClose}
      defaultName={value}
      registrationIdToCopyAnswersFrom={registrationId}
      onCreated={dpia => onCreated({ value: dpia.id, label: dpia.name })}
    />
  );
};
